/**
 * Master data rules and form shapes, client side (D-142).
 *
 * The authority is `onesphere/engines/master.py`: the endpoint re-runs every rule and refuses the save if one
 * blocks, so nothing here can be bypassed by editing the page. This mirror exists so the person filling the form is
 * told *which field* is wrong, in their own language, before they submit — and so the four forms are one screen
 * driven by the field lists below rather than four screens that drift apart.
 *
 * Keep the two in step: the codes are the same codes, and `master.test.ts` pins the pairs that matter.
 */

export type Severity = "error" | "warning";
export type Problem = { field: string; code: string; severity: Severity; params?: Record<string, unknown> };
export type Values = Record<string, unknown>;

/** What can be created here. A vehicle is an asset with a plate, not a kind of its own. */
export const KINDS = ["item", "asset", "customer", "supplier"] as const;
export type Kind = (typeof KINDS)[number];

export const CUSTOMER_TYPES = ["Company", "Individual"] as const;
export const SUPPLIER_TYPES = ["Company", "Individual"] as const;
export const FUEL_TYPES = ["Petrol", "Diesel", "Natural Gas", "Electric"] as const;

export type FieldKind = "text" | "date" | "number" | "money" | "email" | "tel" | "area" | "pick" | "check";

export type FieldSpec = {
  field: string;
  kind: FieldKind;
  /** The picker's option list, by its name in the endpoint's `form_options` answer. */
  options?: string;
  /** A fixed option list, for the ones the ERP does not keep in a DocType. */
  choices?: readonly string[];
  required?: boolean;
  /** Spans the whole row (descriptions and addresses). */
  span?: boolean;
};

export type Section = { section: string; fields: FieldSpec[]; when?: (v: Values) => boolean };

const CODE_RE = /^[A-Za-z0-9][A-Za-z0-9 ._/+-]*$/;
const EMAIL_RE = /^[^@\s]+@[^@\s]+\.[^@\s]+$/;

/** The item form: what a spare part, a consumable or the item behind a fixed asset has to say. */
const ITEM: Section[] = [
  {
    section: "what",
    fields: [
      { field: "item_code", kind: "text", required: true },
      { field: "item_name", kind: "text", required: true },
      { field: "item_group", kind: "pick", options: "item_groups", required: true },
      { field: "stock_uom", kind: "pick", options: "uoms", required: true },
      { field: "brand", kind: "pick", options: "brands" },
      { field: "standard_rate", kind: "money" },
      { field: "description", kind: "area", span: true },
    ],
  },
  {
    section: "kind",
    fields: [
      { field: "is_stock_item", kind: "check" },
      { field: "is_fixed_asset", kind: "check" },
      { field: "asset_category", kind: "pick", options: "asset_categories" },
    ],
  },
];

const ASSET: Section[] = [
  {
    section: "what",
    fields: [
      { field: "asset_name", kind: "text", required: true, span: true },
      { field: "item_code", kind: "pick", options: "items", required: true },
      { field: "asset_category", kind: "pick", options: "asset_categories" },
      { field: "serial_no", kind: "text" },
    ],
  },
  {
    section: "money",
    fields: [
      { field: "company", kind: "pick", options: "companies", required: true },
      { field: "purchase_date", kind: "date", required: true },
      { field: "gross_purchase_amount", kind: "money", required: true },
      { field: "available_for_use_date", kind: "date" },
      { field: "cost_center", kind: "pick", options: "cost_centers" },
    ],
  },
  {
    section: "where",
    fields: [
      { field: "location", kind: "pick", options: "locations" },
      { field: "custodian", kind: "pick", options: "employees" },
    ],
  },
  {
    section: "vehicle",
    when: (v) => Boolean(v.is_vehicle),
    fields: [
      { field: "license_plate", kind: "text", required: true },
      { field: "make", kind: "text", required: true },
      { field: "model", kind: "text", required: true },
      { field: "fuel_type", kind: "pick", choices: FUEL_TYPES, required: true },
      { field: "uom", kind: "pick", options: "uoms", required: true },
      { field: "last_odometer", kind: "number", required: true },
      { field: "chassis_no", kind: "text" },
      { field: "color", kind: "text" },
      { field: "insurance_company", kind: "text" },
      { field: "policy_no", kind: "text" },
      { field: "end_date", kind: "date" },
      { field: "carbon_check_date", kind: "date" },
    ],
  },
];

const CUSTOMER: Section[] = [
  {
    section: "who",
    fields: [
      { field: "customer_name", kind: "text", required: true, span: true },
      { field: "customer_group", kind: "pick", options: "customer_groups", required: true },
      { field: "territory", kind: "pick", options: "territories", required: true },
      { field: "customer_type", kind: "pick", choices: CUSTOMER_TYPES },
      { field: "tax_id", kind: "text" },
      { field: "default_currency", kind: "pick", options: "currencies" },
    ],
  },
  {
    section: "reaching",
    fields: [
      { field: "mobile_no", kind: "tel" },
      { field: "email_id", kind: "email" },
      { field: "website", kind: "text" },
      { field: "customer_details", kind: "area", span: true },
    ],
  },
  {
    // the four things the order rules read (OS-0671). The credit limit is Finance's: for anybody else the endpoint
    // stores it as a proposal, and the form labels it so (MasterForm, `may_set_credit`).
    section: "terms",
    fields: [
      { field: "payment_terms", kind: "pick", options: "payment_terms" },
      { field: "default_price_list", kind: "pick", options: "price_lists" },
      { field: "sales_person", kind: "pick", options: "sales_persons" },
      { field: "credit_company", kind: "pick", options: "companies" },
      { field: "credit_limit", kind: "money" },
    ],
  },
];

const SUPPLIER: Section[] = [
  {
    section: "who",
    fields: [
      { field: "supplier_name", kind: "text", required: true, span: true },
      { field: "supplier_group", kind: "pick", options: "supplier_groups", required: true },
      { field: "supplier_type", kind: "pick", choices: SUPPLIER_TYPES },
      { field: "country", kind: "pick", options: "countries" },
      { field: "tax_id", kind: "text" },
      { field: "default_currency", kind: "pick", options: "currencies" },
    ],
  },
  {
    section: "reaching",
    fields: [
      { field: "mobile_no", kind: "tel" },
      { field: "email_id", kind: "email" },
      { field: "website", kind: "text" },
      { field: "supplier_details", kind: "area", span: true },
    ],
  },
];

export const FORM: Record<Kind, Section[]> = { item: ITEM, asset: ASSET, customer: CUSTOMER, supplier: SUPPLIER };

/** The fields that belong to the `Vehicle` record rather than to the `Asset` — the endpoint takes them apart. */
export const VEHICLE_SECTION = "vehicle";

export function sectionsFor(kind: Kind, values: Values): Section[] {
  return FORM[kind].filter((s) => !s.when || s.when(values));
}

/** The vehicle half of what the form holds, or null when this asset is not a vehicle. */
export function vehicleValues(values: Values): Values | null {
  if (!values.is_vehicle) return null;
  const spec = FORM.asset.find((s) => s.section === VEHICLE_SECTION);
  const out: Values = {};
  for (const f of spec?.fields ?? []) if (values[f.field] !== undefined && values[f.field] !== "") out[f.field] = values[f.field];
  return out;
}

/** What goes to the record itself: everything the form holds that is not the vehicle's and not the form's own flag. */
export function recordValues(kind: Kind, values: Values): Values {
  const vehicleFields = new Set((FORM.asset.find((s) => s.section === VEHICLE_SECTION)?.fields ?? []).map((f) => f.field));
  const out: Values = {};
  for (const section of FORM[kind]) {
    if (section.section === VEHICLE_SECTION) continue;
    for (const f of section.fields) {
      const v = values[f.field];
      if (v !== undefined && v !== "" && !vehicleFields.has(f.field)) out[f.field] = v;
    }
  }
  return out;
}

const problem = (field: string, code: string, severity: Severity = "error", params?: Record<string, unknown>): Problem =>
  ({ field, code, severity, ...(params ? { params } : {}) });

const text = (values: Values, key: string) => (typeof values[key] === "string" ? (values[key] as string).trim() : values[key] == null ? "" : String(values[key]));

/**
 * Everything wrong with what the form holds, worst first — the same codes `engines/master.py` returns.
 *
 * Deliberately not every rule the server has: the ones that need the site's own data (is this code already an item,
 * is this plate already on the road) belong to the endpoint, which has it. What is here is what the person can fix
 * without waiting for an answer.
 */
export function validate(kind: Kind, values: Values, today = new Date()): Problem[] {
  const out: Problem[] = [];
  for (const section of sectionsFor(kind, values)) {
    for (const f of section.fields) {
      if (f.required && !text(values, f.field)) out.push(problem(f.field, "required"));
      if (f.kind === "email" && text(values, f.field) && !EMAIL_RE.test(text(values, f.field))) out.push(problem(f.field, "bad_email", "warning"));
      if ((f.kind === "money" || f.kind === "number") && text(values, f.field)) {
        const n = Number(text(values, f.field).replace(/,/g, ""));
        if (Number.isNaN(n)) out.push(problem(f.field, "not_a_number"));
        else if (n < 0) out.push(problem(f.field, "negative"));
      }
    }
  }
  if (kind === "item") {
    const code = text(values, "item_code");
    if (code && !CODE_RE.test(code)) out.push(problem("item_code", "bad_code"));
    if (values.is_stock_item && values.is_fixed_asset) out.push(problem("is_fixed_asset", "both_stock_and_fixed_asset"));
    if (values.is_fixed_asset && !text(values, "asset_category")) out.push(problem("asset_category", "asset_category_required"));
  }
  if (kind === "customer" && text(values, "credit_limit") && !text(values, "credit_company")) out.push(problem("credit_company", "credit_company_required"));
  if (kind === "asset") {
    const purchase = text(values, "purchase_date");
    const useFrom = text(values, "available_for_use_date");
    if (purchase && new Date(purchase) > today) out.push(problem("purchase_date", "in_the_future"));
    if (purchase && useFrom && new Date(useFrom) < new Date(purchase)) out.push(problem("available_for_use_date", "before_purchase"));
    if (text(values, "gross_purchase_amount") && Number(text(values, "gross_purchase_amount")) === 0) out.push(problem("gross_purchase_amount", "zero"));
    if (!text(values, "location") && !text(values, "custodian")) out.push(problem("location", "no_custodian_or_location"));
    if (values.is_vehicle) {
      const plate = text(values, "license_plate");
      if (plate && !CODE_RE.test(plate)) out.push(problem("license_plate", "bad_code"));
      if (!text(values, "insurance_company") || !text(values, "policy_no")) out.push(problem("policy_no", "no_insurance", "warning"));
      if (!text(values, "carbon_check_date")) out.push(problem("carbon_check_date", "no_technical_inspection", "warning"));
    }
  }
  return out;
}

export const blocking = (problems: Problem[]) => problems.filter((p) => p.severity === "error");
export const forField = (problems: Problem[], field: string) => problems.filter((p) => p.field === field);

/** Where a record of this kind lands in OneFleet once it is pulled — shown on the done panel. */
export const FLEET_BACK: Record<Kind, string> = {
  item: "/fleet/maintenance/parts",
  asset: "/fleet/vehicles",
  customer: "/fleet/partners/customers",
  supplier: "/fleet/partners/vendors",
};

/** Where *Cancel* and *Back to where I was* go when the form was opened without `?from=`, in order of preference,
 * each with the module the person must be able to open. A customer is a sales record first: a representative who
 * typed `/customers/new` used to be sent to the fleet module, which their role does not open (OS-0671). */
const BACK_ORDER: Record<Kind, { to: string; module: string }[]> = {
  item: [{ to: FLEET_BACK.item, module: "fleet" }, { to: "/warehouse", module: "inventory" }],
  asset: [{ to: FLEET_BACK.asset, module: "fleet" }],
  customer: [{ to: "/orders?tab=orders", module: "orders" }, { to: FLEET_BACK.customer, module: "fleet" }],
  supplier: [{ to: FLEET_BACK.supplier, module: "fleet" }, { to: "/procure?tab=suppliers", module: "procurement" }],
};

/** The screen to return to: `?from=` when it is a path of this application (never another site), else the first
 * screen of `BACK_ORDER` whose module the person can open, else home. */
export function backTarget(kind: Kind, from: string | null | undefined, canOpen: (module: string) => boolean): string {
  if (from && from.startsWith("/") && !from.startsWith("//")) return from;
  return BACK_ORDER[kind].find((b) => canOpen(b.module))?.to ?? "/";
}

/** The return link once the record exists: back to an order form, it carries the new customer so the form is
 * filled in with it (`/orders?tab=orders&customer=…`). */
export function returnTo(back: string, kind: Kind, made: string | null | undefined): string {
  if (kind !== "customer" || !made || !back.startsWith("/orders")) return back;
  const [path, query = ""] = back.split("?");
  const params = new URLSearchParams(query);
  params.set("customer", made);
  return `${path}?${params.toString()}`;
}
